import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { DatePicker } from "./DatePicker";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";

const emptyBanner = {
  title: "",
  text: "",
  image_url: "",
  channel: "grocery",
  category: "",
  active: true,
};

const Labeled = ({ label, children }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    {children}
  </div>
);

/** Pengelola banner promo halaman depan (judul ID/JA, jadwal, gambar). */
export const BannersCard = ({ s, setS }) => {
  const { t } = useI18n();
  const banners = s.banners || [];
  const setBanners = (list) => setS({ ...s, banners: list });

  const upload = async (i, file, patch) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patch({ image_url: data.url });
    } catch (err) {
      toast.error(errMsg(err, "Gagal mengunggah foto"));
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4"
      data-testid="banner-manager"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-display font-semibold">{t("banners")}</p>
        <Button
          size="sm"
          variant="outline"
          data-testid="add-banner-btn"
          onClick={() => setBanners([...banners, { ...emptyBanner }])}
        >
          <Plus size={15} className="mr-1" /> {t("banner_add")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("banner_hint")}</p>
      <p className="text-xs text-muted-foreground">{t("banner_seasonal")}</p>
      {banners.length === 0 && <p className="text-sm text-muted-foreground">{t("banner_none")}</p>}
      {banners.map((b, i) => {
        const patch = (p) => setBanners(banners.map((x, j) => (j === i ? { ...x, ...p } : x)));
        return (
          <div
            key={`banner-row-${i}`}
            className="border border-gray-200 rounded-xl p-4 space-y-3"
            data-testid={`banner-row-${i}`}
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <Labeled label="Judul (Indonesia)">
                <Input
                  data-testid={`banner-title-${i}`}
                  placeholder={t("banner_title")}
                  value={b.title}
                  onChange={(e) => patch({ title: e.target.value })}
                />
              </Labeled>
              <Labeled label="Teks (Indonesia)">
                <Input
                  data-testid={`banner-text-${i}`}
                  placeholder={t("banner_text")}
                  value={b.text}
                  onChange={(e) => patch({ text: e.target.value })}
                />
              </Labeled>
              <Labeled label="Judul (Jepang)">
                <Input
                  data-testid={`banner-title-ja-${i}`}
                  placeholder="日本語の見出し"
                  value={b.title_ja || ""}
                  onChange={(e) => patch({ title_ja: e.target.value })}
                />
              </Labeled>
              <Labeled label="Teks (Jepang)">
                <Input
                  data-testid={`banner-text-ja-${i}`}
                  placeholder="日本語の説明"
                  value={b.text_ja || ""}
                  onChange={(e) => patch({ text_ja: e.target.value })}
                />
              </Labeled>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["grocery", t("go_store")],
                  ["resto", t("go_resto")],
                ].map(([c, label]) => (
                  <button
                    key={c}
                    data-testid={`banner-channel-${i}-${c}`}
                    onClick={() => patch({ channel: c })}
                    className={`py-2 rounded-xl text-sm font-medium border transition-colors duration-200 ${
                      b.channel === c
                        ? "bg-gray-900 text-white border-transparent"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Input
                data-testid={`banner-category-${i}`}
                placeholder={t("banner_category")}
                value={b.category}
                onChange={(e) => patch({ category: e.target.value })}
              />
              <DatePicker
                testid={`banner-start-${i}`}
                className="h-10 w-full"
                value={b.start_date || ""}
                placeholder={t("banner_start")}
                onChange={(v) => patch({ start_date: v })}
              />
              <DatePicker
                testid={`banner-end-${i}`}
                className="h-10 w-full"
                value={b.end_date || ""}
                placeholder={t("banner_end")}
                onChange={(v) => patch({ end_date: v })}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {b.image_url && (
                <img src={b.image_url} alt="" className="h-14 w-24 rounded-lg object-cover" />
              )}
              <label
                className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium cursor-pointer"
                data-testid={`banner-upload-label-${i}`}
              >
                {t("upload_photo")}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid={`banner-upload-${i}`}
                  onChange={(e) => upload(i, e.target.files?.[0], patch)}
                />
              </label>
              <Switch
                data-testid={`banner-active-${i}`}
                checked={b.active !== false}
                onCheckedChange={(v) => patch({ active: v })}
              />
              <span className="text-sm text-muted-foreground">{t("promo_active")}</span>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive ml-auto"
                data-testid={`banner-delete-${i}`}
                onClick={() => setBanners(banners.filter((_, j) => j !== i))}
              >
                <X size={15} />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
